<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';
	//Formatters
	import { format } from 'src/services/i18n';
	//Mobile Services
	import {
		include_device_ios,
		include_bluetooth_device_ios,
		abort_connection_ios,
		stop_bluetooth_search_ios,
		disconnect_from_device_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		abort_connection_android,
		disconnect_from_device_android,
		include_device_android,
		include_wifi_device,
		stop_bluetooth_search_android,
	} from 'src/services/native_container_requests_android';
	//Stores
	import { user_data } from 'src/stores/account/user';
import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
import { android_version, app, bluetooth, os } from 'src/stores/session/native-bridge';
	import {
		inclusionStarted,
		inclusionStateCount,
		includeDeviceModal,
		inclusionState,
		includingDeviceData,
	} from 'src/stores/features/inclusion';
	import { saved_wifi_neworks } from 'src/stores/features/wifi/passwords';

	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';

	import { setWizardSteps, updateWizardStepOnPrevNext } from '../../Lists/list-helpers';

	//Design system components
	import {
		Modal,
		Container,
		Wizard,
		WizardItem,
		WizardContainer,
		WizardContent,
		Button,
		Loader,
		Text,
		Alert,
		toast,
	} from '@Shelly/shelly-design-system';

	//App components
	import DeviceByTypeSelect from './IncludeSteps/DeviceByTypeSelect.svelte';
	import InclusionLastStep from './IncludeSteps/InclusionLastStep.svelte';
	import DeviceFromListSelect from './IncludeSteps/DeviceFromListSelect.svelte';

	import {
		closeInclusionModal,
		isOwned,
		translate_single_device_container_data_gble,
	} from 'src/helpers/inclusion_helpers';

	import { is_gen_2, real_id } from 'src/helpers/interface_helpers';

	import { INCLUSION_HIDE_AP_DISABLE, INCLUSION_HIDE_BLUETOOTH_SETTINGS } from 'src/constants';

	import { compare } from 'compare-versions';
	import { mobile } from 'src/services/mobile-rework';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import type { Device } from 'src/types/Device';

	import { isDevServerUser } from 'src/helpers/user_server_conditions';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { addDeviceModal, authLockedDeviceModal } from 'src/stores/device-runtime/firmware-ui';

	// import { createEventDispatcher } from 'svelte';
	// const dispatch = createEventDispatcher();

	let isOpen = false;

	/**
	 * This function clears a previously set timeout and sets the timeout variable to null.
	 * If the environment is in development mode, it also logs a message to the console.
	 */
	const clearTimeoutFailTimer = () => {
		if (import.meta.env.DEV) {
			console.log('⏰ timer cleared');
		}
		clearTimeout(inclusionData.timeout);
		inclusionData.timeout = null;
	};

	/**
	 * @function timeoutFailTimer is used to stop the inclusion process if statuses didn't change within a specified time.
	 * @param time is the duration in milliseconds to wait for a status change before stopping the inclusion process. Default is 90000ms (1.5 minutes).
	 *
	 * If the environment is in development mode, it logs a message to the console when the timer starts.
	 * If a timeout is already in progress, it clears the previous timeout before starting a new one.
	 *
	 * When the timeout expires, it displays a toast notification with an error message, resets the includingDeviceData,
	 * sets inclusionStarted and includeDeviceModal to false, and clears the timeout.
	 */
	const timeoutFailTimer = (time = 90000) => {
		if (import.meta.env.DEV) {
			console.log('⏰ timer started', inclusionData.timeout);
		}
		if (inclusionData.timeout !== null) {
			clearTimeoutFailTimer();
		}

		inclusionData.timeout = setTimeout(() => {
			if (import.meta.env.DEV || isDevServerUser(['repo', 't1', 'nb'])) {
				toast.open({
					type: 'danger',
					title: $format('inclusion_timeout_error', {
						default: 'inclusion process timed out.',
					}),
				});
			}

			includingDeviceData.reset();
			inclusionStarted.set(false);
			// includeDeviceModal.set(false);

			inclusionData.attempts++;
			inclusionData.running = false;
			inclusionData.error = true;
			inclusionData.errorCode = 823;

			clearTimeoutFailTimer();
			// stopBluetoothScanner();
		}, time); //one minute delay if there is no status change
	};

	const inclusionDataDefault = {
		selectVia: 'ble',
		selected: undefined,
		selectedType: undefined,
		notEdited: undefined,
		wifi: {
			ssid: '',
			pass: '',
			savePass: false,
			userConfirmed: false, //this k/v pair is to hide auto connect alert, but to prevent clearing savePass
			apDisable: true,
			errors: {
				ssid: false,
				pass: false,
				selected: false,
			},
		},
		bluetooth: {
			scanStarted: false,
			connecting: false,
			connected: true,
			bleDisable: true,
		},
		device: {},
		callback: undefined,
		error: undefined,
		errorCode: undefined,
		running: false,
		attempts: 0,
		timeout: null,
		timeoutStart: timeoutFailTimer,
		timeoutClear: clearTimeoutFailTimer,
	};

	const isIOS = $os === 'IOS';

	let inclusionData = Object.assign({}, inclusionDataDefault);

	$: isGBLE = inclusionData?.selectVia === 'ble' && inclusionData?.selected?.gen === 'GBLE';

	$: validations = [
		false,
		!inclusionData.selectVia || (isGBLE && $inclusionState?.id === 6),
		!inclusionData.selected,
		!inclusionData.wifi.ssid ||
			(inclusionData.wifi.pass.length < 8 && !isOpen) ||
			(!isOpen && inclusionData.wifi.pass.length === 0) ||
			inclusionData.wifi.errors.ssid ||
			inclusionData.wifi.errors.pass ||
			inclusionData.running,
	];

	let modalRef: Modal = null;
	let wizardItemsContainerRef: HTMLElement = null;

	let wizardItems = setWizardSteps([
		$format('include_by_title', { default: 'include by' }),
		$format('include_device', { default: 'select device' }),
		$format('include_wifi_credentials', { default: 'wifi credential' }),
	]);

	$: if (inclusionData?.selectVia === 'wifi_scan') {
		if (wizardItems.length > 2) {
			wizardItems.pop();
		}
	} else {
		if (wizardItems.length < 3) {
			wizardItems.push({
				title: $format('include_wifi_credentials', {
					default: 'wifi credential',
				}),
				active: false,
				disabled: false,
				showMoreDots: false,
				stepNumber: 3,
				tooltipTitle: `Step 3`,
				activeIndex: 2,
			});
		}
	}

	let activeWizardStep = wizardItems[0];

	const onCloseInclusionModal = () => {
		if (import.meta.env.DEV) console.log('onCloseInclusionModal');
		if (inclusionData?.selectVia === 'ble') {
			if (isIOS) {
				stop_bluetooth_search_ios();
				disconnect_from_device_ios();
				// abort_connection_ios();
			} else {
				stop_bluetooth_search_android();
				disconnect_from_device_android();
				// abort_connection_android();
			}
		} else if (inclusionData?.selectVia === 'wifi_scan') {
			addDeviceModal.set(false);
		}
		inclusionData.timeoutClear();
		closeInclusionModal();
	};

	//#region Wizard Functions
	/**
	 * Tracks which step's content is actually mounted.
	 * On iOS, lags behind activeWizardStep by ~350ms so the slide animation runs
	 * without competing JS work (component mount) on the same thread.
	 */
	let mountedStepIndex = 0;
	let stepMountTimer: ReturnType<typeof setTimeout>;

	const scheduleMountStep = (index: number, immediate = false) => {
		clearTimeout(stepMountTimer);
		if (immediate || !isIOS) {
			mountedStepIndex = index;
		} else {
			stepMountTimer = setTimeout(() => {
				mountedStepIndex = index;
			}, 350);
		}
	};

	const onClickWizardItem = (index: number) => {
		wizardItems = wizardItems.map((item, i) => {
			if (i === index) {
				activeWizardStep = {
					...item,
					activeIndex: i,
				};

				return {
					...item,
					active: true,
				};
			}

			return {
				...item,
				active: false,
			};
		});
		scheduleMountStep(index);
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		if (import.meta.env.DEV) {
			console.log('onClickPrevNextButton', activeWizardStep);
		}

		if (!isIOS && inclusionData.selectVia == 'wifi') {
			const minAndroidVersion = '1.17.1';

			if (compare($android_version, minAndroidVersion, '<')) {
				if (activeWizardStep.activeIndex === 0 && type === 'next') {
					push('/include_devices');
					includeDeviceModal.update((state) => {
						return (state = { ...state, show: false });
					});
					return;
				}
			}
		}

		// when going from second (1) step to the third (2) (in case of GBLE that is finishing the process)
		if (activeWizardStep.activeIndex == 1 && inclusionData.selectVia == 'ble' && type == 'next') {
			const device = inclusionData.selected;

			if (typeof device !== 'undefined' && typeof device !== 'string') {
				includingDeviceData.reset();
				includingDeviceData.setData({ ...device });
			}

			// start connecting to the selected device
			if (is_gen_2(device)) {
				inclusionData.bluetooth.connecting = true;
				if (isIOS) {
					include_bluetooth_device_ios(inclusionData.selected.id, 'connect');
				} else {
					include_device_android(inclusionData.selected.mac, 'connect');
				}
			}

			// set device data for account association at second step (1) (end) (no Wi-Fi to set up)
			if (device.gen === 'GBLE') {
				if (!translate_single_device_container_data_gble(device)) {
					toast.open({
						type: 'danger',
						title: 'Trying to add unknown Shelly BLU device. Please try again to confirm. If this error persists contact support to verify the device authenticity.',
					});
				}
			}
		}

		// stop bluetooth scanner and clear selection when going back to first step (0) from second step (1)
		if (
			activeWizardStep.activeIndex == 1 &&
			// inclusionData.selectVia == 'ble' &&
			type == 'prev'
		) {
			inclusionData.selected = undefined;

			if (inclusionData.bluetooth.scanStarted) {
				if (isIOS) {
					stop_bluetooth_search_ios();
				} else {
					stop_bluetooth_search_android();
				}
				inclusionData.bluetooth.scanStarted = false;
			}
			includingDeviceData.reset();
		}

		// remove the connecting state when going back to second step (1) from third step (2)
		if (activeWizardStep.activeIndex == 2 && inclusionData.selectVia == 'ble' && type == 'prev') {
			if (inclusionData.bluetooth.connecting) {
				inclusionData.bluetooth.connecting = false;
			}
			if (inclusionData.bluetooth.connected) {
				if (isIOS) {
					disconnect_from_device_ios();
					inclusionData.bluetooth.connected = false;
				} else {
					stop_bluetooth_search_android();
					inclusionData.selected = undefined;
					inclusionData.bluetooth.scanStarted = false;
					includingDeviceData.reset();
				}
			}
			clearTimeoutFailTimer();
		}

		// remove the connecting state when going back to second step (1) from third step (2)
		if (
			activeWizardStep.activeIndex == 2 &&
			inclusionData.selectVia == 'ble' &&
			inclusionData.bluetooth.connecting &&
			type == 'prev'
		) {
			inclusionData.bluetooth.connecting = false;
		}

		// handle error when going back to second step (1) from third step (2)
		if (activeWizardStep.activeIndex == 2 && type == 'prev' && inclusionData.error) {
			if (inclusionData?.selectVia == 'ble') {
				if (isIOS) {
					abort_connection_ios();
				} else {
					abort_connection_android();
				}
			}

			if (isGBLE) {
				includingDeviceData.updateData({ buttonPressed: false });
			}

			inclusionStarted.set(false);
			inclusionData.running = false;
			inclusionData.error = false;

			inclusionState.set({
				id: -1,
				value: '',
				type: 'status',
			});
		}

		const result = updateWizardStepOnPrevNext(type, wizardItems, activeWizardStep);

		wizardItems = result.wizardItems;
		activeWizardStep = result.activeWizardStep;
		scheduleMountStep(result.activeWizardStep.activeIndex);

		inclusionData.error = false;

		// changeNextButton();
		if (inclusionData.selectVia !== 'wifi_scan') {
			if (isGBLE && wizardItems[2].title !== $format('bluetooth_inclusion')) {
				wizardItems[2].title = $format('bluetooth_inclusion', {
					default: 'bluetooth inclusion',
				});
			} else if (!isGBLE && wizardItems[2].title !== $format('include_wifi_credentials')) {
				wizardItems[2].title = $format('include_wifi_credentials', {
					default: 'wifi credential',
				});
			}
		}
	};

	const onFinish = async () => {
		if (import.meta.env.DEV) console.log('onFinish');
		const _device = ($includingDeviceData || $includeDeviceModal.selected) as Device;

		// if (!isIOS && inclusionData.error && $inclusionState.id == 18) {
		// 	disconnect_from_device_android();
		// 	stop_bluetooth_search_android();
		// 	await wait(1000);
		// 	search_bluetooth_devices_android();
		// 	include_device_android(inclusionData.selected.mac, 'connect');
		// 	await wait(1000);
		// }

		// device id tp lower case so that we can add it as we store them all low...
		if (!isGBLE) {
			_device.id = _device?.id?.toLowerCase();
		}

		// Remember WiFi Pass
		// Check if $saved_wifi_neworks doesn't has key that is current user e-mail then create new key ans save the credentials, else update the key with new credentials
		if (inclusionData.wifi.savePass) {
			inclusionData.wifi.userConfirmed = true;
			// I don't check if there is an old wifi credentials because onMount is refactored with the new structure.
			if ($saved_wifi_neworks !== null && !$saved_wifi_neworks.hasOwnProperty($user_data.email)) {
				$saved_wifi_neworks[$user_data.email] = {
					ssid: inclusionData.wifi.ssid,
					pass: inclusionData.wifi.pass,
				};
			} else {
				saved_wifi_neworks.update($user_data.email, {
					ssid: inclusionData.wifi.ssid,
					pass: inclusionData.wifi.pass,
				});
			}
		} else {
			if (
				$saved_wifi_neworks !== null &&
				$saved_wifi_neworks.hasOwnProperty($user_data?.email) &&
				!inclusionData.wifi.userConfirmed
			) {
				saved_wifi_neworks.remove($user_data.email); // delete saved wifi credentials for this user email;
			}
		}

		inclusionState.set({
			id: -1,
			value: capFirstChar(
				$format('start_inclusion_state', {
					default: 'starting inclusion process',
				}),
			),
		});

		// set the inclusion state count to 0 and start the inclusion process

		inclusionStateCount.set(0);
		inclusionStarted.set(true);
		inclusionData.running = true;
		inclusionData.error = false;

		if (inclusionData.selectVia == 'ble' && inclusionData.selected?.gen !== 'GBLE') {
			// handle devices that have special rules for those inclusion params
			includingDeviceData.updateData({
				disableBluetooth: INCLUSION_HIDE_BLUETOOTH_SETTINGS.includes(
					inclusionData.selected?.type,
				)
					? false
					: inclusionData.bluetooth.bleDisable,
			});

			includingDeviceData.updateData({
				disableWiFiAp: INCLUSION_HIDE_AP_DISABLE.includes(inclusionData.selected?.type)
					? false
					: inclusionData.wifi.apDisable,
			});

			if (isIOS) {
				include_bluetooth_device_ios(
					inclusionData.selected.id,
					'include',
					undefined,
					undefined,
					inclusionData.wifi.ssid,
					inclusionData.wifi.pass,
				);
			} else {
				include_device_android(
					inclusionData.selected.mac,
					'include',
					inclusionData.wifi.ssid,
					inclusionData.wifi.pass,
				);
			}
			// await wait(4000);
		} else if (inclusionData.selectVia == 'wifi' && !isIOS) {
			include_wifi_device(
				inclusionData.selected.shellySSID,
				inclusionData.wifi.ssid,
				inclusionData.wifi.pass,
			);
		} else if (inclusionData.selectVia !== 'ble' && inclusionData.selected?.gen !== 'GBLE') {
			if (isIOS) {
				include_device_ios(
					inclusionData.wifi.ssid,
					inclusionData.wifi.pass,
					inclusionData.selected === 'any'
						? ''
						: inclusionData.selected === 'ShellyPro3EM'
							? inclusionData.selected
							: inclusionData.selected + '-',
				);
			} else {
				include_device_android(
					inclusionData.selected.mac,
					'include',
					inclusionData.wifi.ssid,
					inclusionData.wifi.pass,
				);
			}
		}
	};

	const handleInclusionState = (e: CustomEvent) => {
		const parsed_data = JSON.parse(e.detail[0]);
		// console.log('%c parsed_data', 'color:orange; font-size:22px', parsed_data);

		if (parsed_data?.status === 'connectedToDevice') {
			inclusionData.bluetooth.connecting = false;
			inclusionData.bluetooth.connected = true;
		}

		if (parsed_data?.status === 'connectingAborted') {
			inclusionData.error = true;
		}

		if (
			'undefined' !== typeof parsed_data?.status &&
			parsed_data.status !== 'deviceDiscovered' &&
			!(selectedType === 'ble' && $bluetooth === 'poweredOff')
		) {
			timeoutFailTimer();
		}

		if ('undefined' !== typeof parsed_data?.error || parsed_data.status === 'deviceProcessed') {
			clearTimeoutFailTimer();

			// use this if you want to open TemplatesModal from inclusion process
			// dispatch('finished');
		}
	};

	// #endregion Wizard Function

	const handleBackButton = (e: CustomEvent) => {
		if (!e.cancelable) return;
		if (activeWizardStep.activeIndex > 0) {
			e.preventDefault();
			onClickPrevNextButton('prev');
		}
	};

	onMount(() => {
		if ($includeDeviceModal.step > 0) {
			onClickWizardItem($includeDeviceModal.step);
			mountedStepIndex = $includeDeviceModal.step; // immediate — no animation on initial open

			inclusionData.selectVia = 'ble';
			if ($includeDeviceModal.selected) {
				inclusionData.selected = $includeDeviceModal.selected;
				inclusionData.bluetooth.connecting = true;
				if (isIOS) {
					include_bluetooth_device_ios(inclusionData.selected.id, 'connect');
				} else {
					include_device_android(inclusionData.selected.mac, 'connect');
				}
			}
		}

		if (Object.keys($includingDeviceData).length > 0) {
			includingDeviceData.reset();
		}
		mobile.addEventListener('including_device', handleInclusionState);

		if (mobile.isMobile) {
			mobile.addEventListener('back_button', handleBackButton);
		}

		return () => {
			clearTimeout(stepMountTimer);
			// reset inclusion wizard modal state on close
			includeDeviceModal.set({ show: false, step: 0, selected: undefined });

			// remove the event listener when the component is unmounted
			mobile.removeEventListener('including_device', handleInclusionState);
			if (mobile.isMobile) {
				mobile.removeEventListener('back_button', handleBackButton);
			}
			// clear the fail timer if the component is unmounted
			clearTimeoutFailTimer();
		};
	});

	const checkDeviceOwnership = async () => {
		const deviceId = inclusionData.selected?.displayID || inclusionData.selected?.id;
		const res = await isOwned(deviceId);
		if (res) {
			toast.open({
				type: 'danger',
				title: $format('cloud_connect_owned_err_toast', {
					default: 'The device is already owned by another user\/account.',
				}),
				text: $format('cloud_connect_owned_err_toast_text', {
					values: { deviceId },
					default:
						'If you believe this is a mistake, please contact our support and provide the device ID: {deviceId}',
				}),
			});
		} else {
			if (inclusionData.selectVia === 'wifi_scan') {
				if ($auth_locked_devices.indexOf(real_id(inclusionData?.selected?.id)) !== -1) {
					authLockedDeviceModal.set(inclusionData.selected.id);
				} else {
					includeDeviceModal.set({
						show: false,
						step: 0,
						selected: undefined,
					});
				}
			} else {
				onClickPrevNextButton('next');
			}
		}
	};

	$: selectedType = inclusionData.selectVia;
	$: {
		if (
			$app &&
			$os !== 'IOS' &&
			selectedType === 'ble' &&
			$bluetooth === 'poweredOff' &&
			activeWizardStep.activeIndex === 1 &&
			$controlBleDevice.currentErrorIndex === 9
		) {
			onClickPrevNextButton('prev');
			controlBleDevice.reset();
			clearTimeoutFailTimer();
		}
	}
</script>

<Modal
	on:close={onCloseInclusionModal}
	showModal={$includeDeviceModal.show}
	title={$format('prompt_title_add_device', {
		default: 'Add device',
	})}
	bind:this={modalRef}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i}
			<WizardItem
				on:click={() => {
					onClickWizardItem(i);
				}}
				active={item.active}
				title={item.title}
				disabled={validations[i] || inclusionData.running}
				showMoreDots={item.showMoreDots}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<svelte:fragment slot="content">
		<Container padding="0">
			<WizardContainer
				activeStepIndex={activeWizardStep.activeIndex}
				bind:wizardItemsContainerRef
				adaptiveHeight
				fullHeight={activeWizardStep.activeIndex == 1}
			>
				{#each wizardItems as _, i (i)}
					<!-- DO NOT REMOVE COMMENT: Padding references -->
					<WizardContent active={activeWizardStep.activeIndex === i} class="pb-s">
						<!-- {#if activeWizardStep.activeIndex === i} -->
						{#if i === 0}
							<DeviceByTypeSelect bind:inclusionData />
						{:else if i === 1 && mountedStepIndex == 1}
							<DeviceFromListSelect bind:inclusionData />
						{:else if i === 1 && activeWizardStep.activeIndex == 1 && inclusionData.selectVia === 'ble'}
							<Text element="h2" class="mb-s">
								{$format('inclusion_ble_device_type', { default: 'Bluetooth scan' })}
							</Text>
							<Text class="mb-m" styleType="body-small">
								{$format('inclusion_ble_searching', {
									default: 'Searching for nearby Bluetooth devices.',
								})}
							</Text>
							<Alert
								class="mb-xl"
								type="info"
								isHeadingBold
								heading={$format('label_info')}
								message={$format('alert_message_ble_discovering_description', {
									default:
										'Discovering ble devices requires single button push to appear in device list.',
								})}
							/>
							<Loader size="l" class="d-flex justify-content-center" />
						{:else if i === 1 && activeWizardStep.activeIndex == 1}
							<Loader size="l" class="d-flex justify-content-center mt-l" />
						{:else if i === 2 && mountedStepIndex == 2}
							<InclusionLastStep
								on:save={() => onFinish()}
								bind:inclusionData
								bind:isOpen
							/>
						{/if}
					</WizardContent>
				{/each}
			</WizardContainer>
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		{#if activeWizardStep.activeIndex > 0}
			<Button
				disabled={inclusionData.running}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon={undefined}
				variant="secondary"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={() => onClickPrevNextButton('prev')}
			>
				{$format('button_back', { default: 'back' })}
			</Button>
		{/if}
		{#if inclusionData?.selected?.gen !== 'GBLE' || activeWizardStep.activeIndex !== 2}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				disabled={($os === 'IOS' &&
					selectedType === 'ble' &&
					['unauthorized', 'poweredOff'].includes($bluetooth) &&
					activeWizardStep.activeIndex === 0) ||
					inclusionData.bluetooth.connecting ||
					inclusionData.running ||
					(isGBLE
						? activeWizardStep.activeIndex === 2
						: validations[activeWizardStep.activeIndex + 1])}
				icon={activeWizardStep.activeIndex === 2 && !inclusionData.error ? 'fas fa-plus' : ''}
				rightIcon={activeWizardStep.activeIndex === 2 ? '' : 'fas fa-chevron-right'}
				responsiveSize={{
					sm: 'm',
				}}
				loading={inclusionData.bluetooth.connecting}
				on:click={() => {
					if (inclusionData.selectVia === 'wifi_scan' && activeWizardStep.activeIndex === 1) {
						checkDeviceOwnership();
					} else if (activeWizardStep.activeIndex === wizardItems.length - 1) {
						onFinish();
					} else {
						if (activeWizardStep.activeIndex === 1) {
							checkDeviceOwnership();
						} else {
							onClickPrevNextButton('next');
						}
					}
				}}
			>
				{capFirstChar(
					inclusionData.selectVia === 'wifi_scan' && activeWizardStep.activeIndex === 1
						? $format('button_add_device', { default: 'add device' })
						: activeWizardStep.activeIndex === 2
							? inclusionData.error
								? $format('button_try_again', { default: 'try again' })
								: $format('button_add_device', { default: 'add device' })
							: $format('button_next', { default: 'next' }),
				)}
			</Button>
		{/if}
	</div>
</Modal>
